// src/lib/settle.ts — turning a set of balances into "who pays whom".
//
// trip-balances in the contract: "computes the minimum transfer set: sort
// debtors and creditors by magnitude, repeatedly match largest against
// largest ... Greedy matching is not provably minimal in every case, but it's
// optimal for the group sizes this will ever see and it's what people expect."
//
// Both halves of that are worth keeping in mind. Greedy gives at most n-1
// transfers for n people, which is fine; finding the true minimum is
// NP-hard (it is partition in disguise), and for six people on a trip the
// difference is at most one extra payment nobody would notice.

import { fromCents, toCents } from './money';

export interface MemberBalance {
  memberId: string;
  displayName: string;
  /** Positive = the group owes them. Negative = they owe the group. */
  netBase: number;
}

export interface Transfer {
  fromMemberId: string;
  toMemberId: string;
  baseAmount: number;
}

/** Under a cent either way is settled, not a rounding artefact to chase. */
const DUST_CENTS = 1;

export function minimumTransfers(balances: MemberBalance[]): Transfer[] {
  // Cents throughout, so the loop terminates on exact zero rather than on an
  // epsilon comparison that can leave a 0.004 debt circulating forever.
  const debtors: { id: string; cents: number }[] = [];
  const creditors: { id: string; cents: number }[] = [];

  for (const b of balances) {
    const cents = toCents(b.netBase);
    if (cents < -DUST_CENTS) debtors.push({ id: b.memberId, cents: -cents });
    else if (cents > DUST_CENTS) creditors.push({ id: b.memberId, cents });
  }

  // Largest first; member id breaks ties so the suggested payments don't
  // reshuffle between two people who owe the same amount every time the page
  // reloads.
  const byMagnitude = (a: { id: string; cents: number }, b: { id: string; cents: number }) =>
    b.cents - a.cents || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  debtors.sort(byMagnitude);
  creditors.sort(byMagnitude);

  const transfers: Transfer[] = [];
  let i = 0;
  let j = 0;

  while (i < debtors.length && j < creditors.length) {
    const amount = Math.min(debtors[i].cents, creditors[j].cents);
    if (amount > 0) {
      transfers.push({
        fromMemberId: debtors[i].id,
        toMemberId: creditors[j].id,
        baseAmount: fromCents(amount),
      });
    }
    debtors[i].cents -= amount;
    creditors[j].cents -= amount;
    if (debtors[i].cents <= DUST_CENTS) i++;
    if (creditors[j].cents <= DUST_CENTS) j++;
  }

  return transfers;
}

/**
 * The transfers that involve one particular person — what the settle-up screen
 * shows them first, since "you owe Sam $84.20" is the only line most people
 * read.
 */
export function transfersFor(transfers: Transfer[], memberId: string | null): Transfer[] {
  if (!memberId) return [];
  return transfers.filter((t) => t.fromMemberId === memberId || t.toMemberId === memberId);
}
